import { useEffect, useState } from "react";
import { Upload, ShieldCheck, AlertTriangle, FileText, Copy, Check, Wallet, ArrowDownCircle, ArrowUpCircle, Hourglass } from "lucide-react";
import { api, formatApiError, formatINR } from "../lib/api";
import { toast } from "sonner";

const DOC_TYPES = [
    { v: "aadhaar", l: "Aadhaar Card" },
    { v: "pan", l: "PAN Card" },
    { v: "rera_certificate", l: "RERA Certificate" },
    { v: "bar_council_id", l: "Bar Council ID" },
    { v: "loan_license", l: "Loan Agent License" },
    { v: "company_registration", l: "Company Registration" },
    { v: "other", l: "Other" },
];

const STATUS_STYLES = {
    approved: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", label: "Approved" },
    pending: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", label: "Pending Review" },
    rejected: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200", label: "Rejected" },
    not_submitted: { bg: "bg-navy-700/30", text: "text-navy-500/70", border: "border-navy-500/15", label: "Not Submitted" },
};

export function KYCSection() {
    const [data, setData] = useState({ status: "not_submitted", verified: false, documents: [] });
    const [showForm, setShowForm] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [form, setForm] = useState({ document_type: "aadhaar", document_url: "", doc_number: "", notes: "" });

    const reload = () => {
        api.get("/kyc/me").then((r) => setData(r.data || { status: "not_submitted", documents: [] }));
    };
    useEffect(() => { reload(); }, []);

    const onUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data: r } = await api.post("/kyc/upload-doc", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setForm((f) => ({ ...f, document_url: r.url }));
            toast.success("Document uploaded");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.document_url) { toast.error("Please upload the document file"); return; }
        setSubmitting(true);
        try {
            await api.post("/kyc/submit", form);
            toast.success("KYC submitted — awaiting admin review");
            setShowForm(false);
            setForm({ document_type: "aadhaar", document_url: "", doc_number: "", notes: "" });
            reload();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    const style = STATUS_STYLES[data.status] || STATUS_STYLES.not_submitted;

    return (
        <div data-testid="kyc-section">
            <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-xl font-bold text-navy-500">KYC Verification</h2>
                {!showForm && data.status !== "approved" && (
                    <button
                        type="button"
                        onClick={() => setShowForm(true)}
                        data-testid="kyc-submit-new"
                        className="text-sm px-4 py-2 rounded-md bg-gold-500 text-white font-semibold hover:bg-gold-600 inline-flex items-center gap-2 shadow-sm"
                    >
                        <Upload className="w-4 h-4" /> Submit Document
                    </button>
                )}
            </div>

            <div className={`rounded-xl border ${style.border} ${style.bg} p-5 flex items-start gap-4 mb-4`} data-testid="kyc-status-card">
                <div className={`w-10 h-10 rounded-full ${style.bg} ${style.text} border ${style.border} flex items-center justify-center flex-shrink-0`}>
                    {data.verified ? <ShieldCheck className="w-5 h-5" /> : data.status === "rejected" ? <AlertTriangle className="w-5 h-5" /> : data.status === "pending" ? <Hourglass className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
                </div>
                <div className="flex-1">
                    <div className={`text-xs uppercase tracking-[0.18em] font-semibold ${style.text}`}>{style.label}</div>
                    <div className="mt-1 text-sm text-navy-500/85">
                        {data.status === "approved" && "You are a verified partner. Your profile displays the verified badge."}
                        {data.status === "pending" && "Your documents are under review. We typically review within 24–48 hours."}
                        {data.status === "rejected" && "Your last submission was rejected. Please re-submit a clearer or correct document."}
                        {data.status === "not_submitted" && "Submit your KYC documents to get verified and unlock the verified badge."}
                    </div>
                </div>
            </div>

            {showForm && (
                <form onSubmit={onSubmit} className="rounded-xl bg-white border border-navy-500/10 p-5 space-y-4 mb-4 shadow-sm" data-testid="kyc-form">
                    <div className="flex items-center justify-between">
                        <div className="label-overline">New KYC Submission</div>
                        <button type="button" onClick={() => setShowForm(false)} className="text-xs text-navy-500/60 hover:text-navy-500">Cancel</button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-navy-500/65 mb-1 block font-medium">Document type</label>
                            <select value={form.document_type} onChange={(e) => setForm({ ...form, document_type: e.target.value })} data-testid="kyc-doctype" className="w-full bg-[#FAFAF7] border border-navy-500/15 rounded-md px-3 py-2 text-sm text-navy-500 focus:border-gold-500/60 focus:bg-white focus:outline-none">
                                {DOC_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-navy-500/65 mb-1 block font-medium">Document number</label>
                            <input value={form.doc_number} onChange={(e) => setForm({ ...form, doc_number: e.target.value })} data-testid="kyc-docnumber" placeholder="e.g. AADHAAR / RERA #" className="w-full bg-[#FAFAF7] border border-navy-500/15 rounded-md px-3 py-2 text-sm text-navy-500 placeholder:text-navy-500/40 focus:border-gold-500/60 focus:bg-white focus:outline-none" />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs text-navy-500/65 mb-1 block font-medium">Notes (optional)</label>
                        <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="kyc-notes" className="w-full bg-[#FAFAF7] border border-navy-500/15 rounded-md px-3 py-2 text-sm text-navy-500 focus:border-gold-500/60 focus:bg-white focus:outline-none" />
                    </div>
                    <div>
                        <label className="text-xs text-navy-500/65 mb-1 block font-medium">Document file (PDF / JPG / PNG · max 8MB)</label>
                        <label className="block rounded-md border-2 border-dashed border-navy-500/20 hover:border-gold-500/60 cursor-pointer p-4 text-center bg-[#FAFAF7]">
                            <Upload className="w-5 h-5 mx-auto text-gold-600 mb-1" />
                            <div className="text-xs text-navy-500/65">
                                {uploading ? "Uploading…" : form.document_url ? "Replace file" : "Click to upload"}
                            </div>
                            <input type="file" accept="image/*,application/pdf" onChange={onUpload} className="hidden" data-testid="kyc-file-input" />
                        </label>
                        {form.document_url && <div className="text-xs text-emerald-700 mt-2 inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> File uploaded</div>}
                    </div>
                    <button type="submit" disabled={submitting || uploading} data-testid="kyc-form-submit" className="w-full px-4 py-2.5 rounded-md bg-gold-500 text-white font-semibold text-sm hover:bg-gold-600 disabled:opacity-60 shadow-sm">
                        {submitting ? "Submitting…" : "Submit for Review"}
                    </button>
                </form>
            )}

            {data.documents.length > 0 && (
                <div className="rounded-xl bg-white border border-navy-500/10 overflow-hidden shadow-sm">
                    <div className="px-5 py-3 border-b border-navy-500/10 label-overline">Submission History</div>
                    <div className="divide-y divide-navy-500/10">
                        {data.documents.map((d) => {
                            const st = STATUS_STYLES[d.status] || STATUS_STYLES.pending;
                            return (
                                <div key={d.id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`kyc-doc-${d.id}`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-semibold text-navy-500 capitalize">{(d.document_type || "").replace(/_/g, " ")}</div>
                                        <div className="text-xs text-navy-500/60 mt-0.5">
                                            {d.doc_number && <span className="mr-3">#{d.doc_number}</span>}
                                            <span>{new Date(d.created_at).toLocaleString()}</span>
                                        </div>
                                        {d.review_notes && <div className="text-xs text-navy-500/65 italic mt-1">"{d.review_notes}"</div>}
                                    </div>
                                    <a href={d.document_url} target="_blank" rel="noreferrer" className="text-xs text-gold-700 hover:underline font-medium">View file</a>
                                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${st.bg} ${st.text} ${st.border}`}>{st.label}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

export function WalletSection({ user }) {
    const [data, setData] = useState({ wallet: { balance: 0, total_earned: 0, total_withdrawn: 0 }, transactions: [], referrals: [], pending_withdrawals: [] });
    const [showWithdraw, setShowWithdraw] = useState(false);
    const [wform, setWform] = useState({ amount: "", upi_or_bank: "", notes: "" });
    const [submitting, setSubmitting] = useState(false);
    const [copied, setCopied] = useState(false);

    const reload = () => api.get("/wallet/me").then((r) => setData(r.data));
    useEffect(() => { reload(); }, []);

    const refCode = data.referral_code || user?.referral_code || "";
    const refLink = refCode ? `${window.location.origin}/register?ref=${refCode}` : "";

    const onCopy = async () => {
        try {
            await navigator.clipboard.writeText(refLink);
            setCopied(true);
            toast.success("Referral link copied");
            setTimeout(() => setCopied(false), 1800);
        } catch {
            toast.error("Could not copy");
        }
    };

    const onWithdraw = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/wallet/withdraw", { amount: Number(wform.amount), upi_or_bank: wform.upi_or_bank, notes: wform.notes || null });
            toast.success("Withdrawal request submitted");
            setShowWithdraw(false);
            setWform({ amount: "", upi_or_bank: "", notes: "" });
            reload();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="wallet-section">
            <h2 className="font-heading text-xl font-bold text-navy-500 mb-4">Wallet & Referrals</h2>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
                <div className="text-on-image rounded-2xl bg-gradient-to-br from-[#002657] to-[#1a3a6e] text-white p-6 shadow-md">
                    <div className="flex items-center justify-between">
                        <Wallet className="w-5 h-5 text-gold-300" />
                        <span className="text-[10px] uppercase tracking-widest text-gold-300/90 font-semibold">Withdrawable Balance</span>
                    </div>
                    <div className="font-heading text-4xl font-bold mt-3 text-white" data-testid="wallet-balance">{formatINR(data.wallet?.balance || 0)}</div>
                    <div className="text-xs text-white/65 mt-1">Earned: {formatINR(data.wallet?.total_earned || 0)} · Withdrawn: {formatINR(data.wallet?.total_withdrawn || 0)}</div>
                    {(data.wallet?.bonus_balance || 0) > 0 && (
                        <div className="mt-3 px-3 py-2 rounded-lg bg-gold-500/15 border border-gold-300/30 flex items-start gap-2" data-testid="wallet-bonus-balance">
                            <span className="text-gold-300 mt-0.5">★</span>
                            <div className="leading-tight">
                                <div className="text-xs text-gold-200">+ <span className="font-bold text-white">{formatINR(data.wallet.bonus_balance)}</span> bonus credits</div>
                                <div className="text-[10px] text-white/60">Non-withdrawable · Use on subscription plans</div>
                            </div>
                        </div>
                    )}
                    <button
                        type="button"
                        onClick={() => setShowWithdraw(true)}
                        data-testid="wallet-withdraw-btn"
                        className="mt-4 w-full px-4 py-2 rounded-md bg-gold-500 text-white font-semibold text-sm hover:bg-gold-600 inline-flex items-center justify-center gap-2"
                    >
                        <ArrowUpCircle className="w-4 h-4" /> Request Withdrawal
                    </button>
                </div>

                <div className="lg:col-span-2 rounded-2xl bg-white border border-navy-500/10 p-6 shadow-sm">
                    <div className="label-overline mb-2">Your Referral Code</div>
                    <div className="flex items-center gap-2">
                        <code className="flex-1 font-mono text-2xl font-bold text-navy-500 tracking-wider px-4 py-3 bg-[#FAFAF7] rounded-md border border-navy-500/10" data-testid="referral-code">{refCode || "—"}</code>
                        <button onClick={onCopy} disabled={!refLink} data-testid="copy-referral" className="px-4 py-3 rounded-md border border-gold-500/40 text-gold-700 hover:bg-gold-500/10 inline-flex items-center gap-1.5 disabled:opacity-50">
                            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                            <span className="text-sm font-semibold">{copied ? "Copied" : "Copy link"}</span>
                        </button>
                    </div>
                    <p className="text-xs text-navy-500/65 mt-3">
                        Share your code and earn <span className="font-semibold text-gold-700">₹200</span> bonus credits when someone signs up (usable on subscription plans, non-withdrawable) + an extra <span className="font-semibold text-gold-700">₹1,000</span> withdrawable when they get verified.
                    </p>
                </div>
            </div>

            {showWithdraw && (
                <form onSubmit={onWithdraw} className="rounded-xl bg-white border border-navy-500/10 p-5 space-y-3 mb-4 shadow-sm" data-testid="withdraw-form">
                    <div className="flex items-center justify-between">
                        <div className="label-overline">New Withdrawal Request</div>
                        <button type="button" onClick={() => setShowWithdraw(false)} className="text-xs text-navy-500/60 hover:text-navy-500">Cancel</button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-navy-500/65 mb-1 block font-medium">Amount (₹) · min 100</label>
                            <input type="number" min="100" step="50" required value={wform.amount} onChange={(e) => setWform({ ...wform, amount: e.target.value })} data-testid="withdraw-amount" className="w-full bg-[#FAFAF7] border border-navy-500/15 rounded-md px-3 py-2 text-sm text-navy-500 focus:border-gold-500/60 focus:bg-white focus:outline-none" />
                        </div>
                        <div>
                            <label className="text-xs text-navy-500/65 mb-1 block font-medium">UPI ID or Bank Account</label>
                            <input required value={wform.upi_or_bank} onChange={(e) => setWform({ ...wform, upi_or_bank: e.target.value })} data-testid="withdraw-account" placeholder="e.g. yourname@upi" className="w-full bg-[#FAFAF7] border border-navy-500/15 rounded-md px-3 py-2 text-sm text-navy-500 placeholder:text-navy-500/40 focus:border-gold-500/60 focus:bg-white focus:outline-none" />
                        </div>
                    </div>
                    <button type="submit" disabled={submitting} data-testid="withdraw-submit" className="w-full px-4 py-2.5 rounded-md bg-gold-500 text-white font-semibold text-sm hover:bg-gold-600 disabled:opacity-60">
                        {submitting ? "Submitting…" : "Submit Request"}
                    </button>
                </form>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="rounded-xl bg-white border border-navy-500/10 overflow-hidden shadow-sm">
                    <div className="px-5 py-3 border-b border-navy-500/10 label-overline flex items-center justify-between">
                        <span>Recent Transactions</span>
                        <span className="text-navy-500/50 text-[10px] normal-case">{data.transactions?.length || 0}</span>
                    </div>
                    {(data.transactions || []).length === 0 ? (
                        <div className="px-5 py-8 text-center text-sm text-navy-500/60">No transactions yet. Share your referral code to start earning!</div>
                    ) : (
                        <div className="divide-y divide-navy-500/10 max-h-80 overflow-y-auto">
                            {data.transactions.map((t) => (
                                <div key={t.id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`txn-${t.id}`}>
                                    <div className="flex items-start gap-2 min-w-0">
                                        {t.amount >= 0 ? <ArrowDownCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" /> : <ArrowUpCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />}
                                        <div className="min-w-0">
                                            <div className="text-sm text-navy-500 truncate">{t.description}</div>
                                            <div className="text-[10px] text-navy-500/55 uppercase tracking-wider mt-0.5">{t.type} · {new Date(t.created_at).toLocaleDateString()}</div>
                                        </div>
                                    </div>
                                    <div className={`text-sm font-bold whitespace-nowrap ${t.amount >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                                        {t.amount >= 0 ? "+" : ""}{formatINR(Math.abs(t.amount))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="rounded-xl bg-white border border-navy-500/10 overflow-hidden shadow-sm">
                    <div className="px-5 py-3 border-b border-navy-500/10 label-overline flex items-center justify-between">
                        <span>Your Referrals</span>
                        <span className="text-navy-500/50 text-[10px] normal-case">{data.referrals?.length || 0}</span>
                    </div>
                    {(data.referrals || []).length === 0 ? (
                        <div className="px-5 py-8 text-center text-sm text-navy-500/60">No referrals yet.</div>
                    ) : (
                        <div className="divide-y divide-navy-500/10 max-h-80 overflow-y-auto">
                            {data.referrals.map((r) => (
                                <div key={r.id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`ref-${r.id}`}>
                                    <div className="min-w-0">
                                        <div className="text-sm font-semibold text-navy-500 truncate">{r.referee_name}</div>
                                        <div className="text-xs text-navy-500/60 capitalize">{r.referee_role} · {new Date(r.created_at).toLocaleDateString()}</div>
                                    </div>
                                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${r.status === "verified" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                                        {r.status === "verified" ? "Verified" : "Signed up"}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
